import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Envelope,
  Phone,
  Buildings,
  FacebookLogo,
  InstagramLogo,
  TiktokLogo,
  WhatsappLogo,
  Globe,
  MapPin,
  IdentificationCard,
  Star,
} from '@phosphor-icons/react';

/**
 * Displays the counterpart contact info after both commissions are paid.
 * Replicates the mobile app "Datos de Contacto" screen: company, RFC, dom. fiscal,
 * régimen fiscal, email, phone, specialties (provider only), rating (provider only),
 * and social networks (Facebook, Instagram, TikTok, WhatsApp, Website).
 */
const ContactInfoCard = ({ title = 'Información de Contacto', data, role }) => {
  if (!data) return null;

  const isProvider = role === 'provider';
  const social = data.social_links || {};

  const socials = [
    { key: 'facebook', label: 'Facebook', Icon: FacebookLogo, color: 'text-blue-600' },
    { key: 'instagram', label: 'Instagram', Icon: InstagramLogo, color: 'text-pink-500' },
    { key: 'tiktok', label: 'TikTok', Icon: TiktokLogo, color: 'text-zinc-900 dark:text-zinc-100' },
    { key: 'whatsapp', label: 'WhatsApp', Icon: WhatsappLogo, color: 'text-green-600' },
    { key: 'website', label: 'Sitio Web', Icon: Globe, color: 'text-orange-600' },
  ].map((s) => ({ ...s, value: social[s.key] || data[s.key] }))
   .filter((s) => !!s.value);

  const toHref = (key, val) => {
    if (!val) return null;
    if (val.startsWith('http://') || val.startsWith('https://')) return val;
    if (key === 'whatsapp') {
      const digits = val.replace(/[^0-9]/g, '');
      return digits ? `https://wa.me/${digits}` : null;
    }
    return `https://${val}`;
  };

  return (
    <Card className="border-green-200 dark:border-green-800 rounded-none" data-testid={`contact-info-${role}`}>
      <CardHeader className="border-b border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
        <CardTitle className="text-base flex items-center gap-2 text-green-800 dark:text-green-300">
          <Buildings className="w-5 h-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 space-y-5">
        {/* Identity */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-zinc-500">Nombre / Razón Social</p>
            <p className="font-medium text-zinc-900 dark:text-zinc-100">
              {data.business_name || data.company || data.name || 'N/A'}
            </p>
          </div>
          {data.rfc && (
            <div className="flex items-start gap-2">
              <IdentificationCard className="w-4 h-4 text-zinc-400 mt-1 shrink-0" />
              <div>
                <p className="text-xs text-zinc-500">RFC</p>
                <p className="font-medium text-zinc-900 dark:text-zinc-100">{data.rfc}</p>
              </div>
            </div>
          )}
          {data.fiscal_regime && (
            <div>
              <p className="text-xs text-zinc-500">Régimen Fiscal</p>
              <p className="font-medium text-zinc-900 dark:text-zinc-100 text-sm">{data.fiscal_regime}</p>
            </div>
          )}
          {data.fiscal_address && (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-zinc-400 mt-1 shrink-0" />
              <div>
                <p className="text-xs text-zinc-500">Domicilio / CP</p>
                <p className="font-medium text-zinc-900 dark:text-zinc-100 text-sm">{data.fiscal_address}</p>
              </div>
            </div>
          )}
        </div>

        {/* Contact */}
        <div className="grid sm:grid-cols-2 gap-4">
          {data.email && (
            <div className="flex items-start gap-2">
              <Envelope className="w-4 h-4 text-zinc-400 mt-1 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-zinc-500">Email</p>
                <a href={`mailto:${data.email}`} className="font-medium text-zinc-900 dark:text-zinc-100 hover:text-orange-600 break-all">
                  {data.email}
                </a>
              </div>
            </div>
          )}
          {data.phone && (
            <div className="flex items-start gap-2">
              <Phone className="w-4 h-4 text-zinc-400 mt-1 shrink-0" />
              <div>
                <p className="text-xs text-zinc-500">Teléfono</p>
                <a href={`tel:${data.phone}`} className="font-medium text-zinc-900 dark:text-zinc-100 hover:text-orange-600">
                  {data.phone}
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Provider extras */}
        {isProvider && data.specialties?.length > 0 && (
          <div>
            <p className="text-xs text-zinc-500 mb-1">Especialidades</p>
            <div className="flex flex-wrap gap-1">
              {data.specialties.map((s) => (
                <Badge key={s} variant="outline" className="rounded-none text-xs">{s}</Badge>
              ))}
            </div>
          </div>
        )}
        {isProvider && data.rating_count > 0 && (
          <div className="flex items-center gap-2">
            <Star weight="fill" className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
              {(data.rating_avg || 0).toFixed(1)}
            </span>
            <span className="text-xs text-zinc-500">({data.rating_count} calificaciones)</span>
          </div>
        )}

        {/* Socials */}
        {socials.length > 0 && (
          <div>
            <p className="text-xs text-zinc-500 mb-2">Redes Sociales</p>
            <div className="flex flex-wrap gap-2">
              {socials.map(({ key, label, Icon, color, value }) => {
                const href = toHref(key, value);
                const content = (
                  <span className="inline-flex items-center gap-1.5 text-xs border border-zinc-200 dark:border-zinc-700 px-2.5 py-1.5 bg-white dark:bg-zinc-900 hover:border-orange-400 transition-colors">
                    <Icon className={`w-4 h-4 ${color}`} weight="fill" />
                    <span className="font-medium text-zinc-700 dark:text-zinc-200">{label}</span>
                  </span>
                );
                return href ? (
                  <a key={key} href={href} target="_blank" rel="noreferrer" data-testid={`social-${key}`}>
                    {content}
                  </a>
                ) : (
                  <span key={key} data-testid={`social-${key}`}>{content}</span>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ContactInfoCard;
